import clsx from 'clsx';
import type React from 'react';
import type { AriaAttributes } from 'react';

import { Icon } from '@gitbook/spine-icons';

import { Badge } from '../Badges';
import { useConfigurationTheme } from '../theme';
import styles from './Stepper.module.css';

export interface StepperStep {
    title: string;
    content?: React.ReactNode;
}

export interface StepperProps {
    /**
     * Steps to be rendered in the body of stepper.
     */
    steps: StepperStep[];
    /**
     * Completed steps (enables us to implement skipping steps).
     */
    completedSteps: Set<number>;
    /**
     * Current step position is steps array.
     */
    currentStepIndex: number;
    /**
     * Styles for the wrapper element.
     */
    styles?: string;

    /** Direction of the stepper, horizontal or vertical */
    orientation?: AriaAttributes['aria-orientation'];

    /** Whether to animate the active step */
    animate?: boolean;

    /** List of errored steps */
    erroredSteps?: number[];
}

/**
 * Stepper displays progress through a sequence of numbered steps.
 */
export function Stepper(props: StepperProps): React.ReactElement {
    const {
        steps,
        completedSteps,
        styles: wrapperStyles,
        currentStepIndex,
        orientation = 'horizontal',
        animate = false,
        erroredSteps,
    } = props;
    const theme = useConfigurationTheme();
    const isDarkTheme = theme.color === 'dark';

    const stepIsCurrentStep = (index: number) => index === currentStepIndex;
    const stepIsCompleted = (index: number) => completedSteps.has(index);
    const stepHasError = (index: number) => erroredSteps?.includes(index);
    const stepHasPulseAnimation = (index: number) => currentStepIndex === index && animate;

    // Step number shown within the badge, if undefined it means we show an icon
    const stepNumber = (index: number) =>
        stepIsCurrentStep(index) && !stepIsCompleted(index) && !stepHasError(index)
            ? index + 1
            : undefined;

    // If we don't show a number, we show an icon
    const stepIcon = (index: number) =>
        stepHasError(index)
            ? Icon.AlertStandalone
            : stepIsCompleted(index)
              ? Icon.Check
              : undefined;

    // // This decides what style the lines are (dashed, solid etc). Its a bit more complex cause the 'after' line is always saying something about the next badge/step, while the 'before' line is saying something about the current badge/step.
    const getLineStyle = (index: number, isBeforeBadge: boolean, horizontal = false) => {
        switch (true) {
            // Line is horizontal, before badge & this step has an error, the line should be dashed grey to display an X on top of it
            case (horizontal && isBeforeBadge && stepHasError(index)) ||
                (horizontal && !isBeforeBadge && erroredSteps?.includes(index + 1)):
                return styles.horizontalDashedLine;

            // Line is before badge & this step has an error, the line should be dashed grey to display an X on top of it
            case (isBeforeBadge && stepHasError(index)) ||
                (!isBeforeBadge && erroredSteps?.includes(index + 1)):
                return styles.verticalDashedLine;

            // Line is before badge & this badge is the current or previous step || the current step/badge is completed, so the line should be solid blue
            case (isBeforeBadge && index <= currentStepIndex) ||
                (isBeforeBadge && stepIsCompleted(index)):
                return styles.solidBlueLine;

            // Line is after badge & this badge is a previous step || the next step/badge is completed, so the line should be solid blue
            case (!isBeforeBadge && index < currentStepIndex) ||
                (!isBeforeBadge && completedSteps.has(index + 1)):
                return styles.solidBlueLine;

            // The default line for horizontal steppers is a dashed grey line, but with different dash px lengths
            case horizontal:
                return styles.horizontalDashedLine;

            // The default line for vertical steppers is also a dashed grey line, but with different dash px lengths
            default:
                return styles.verticalDashedLine;
        }
    };

    // Get the background color of the badge depending on context
    const getBackgroundColor = (index: number) => {
        switch (true) {
            case stepIsCurrentStep(index):
                return 'var(--color-background-main)';
            case stepIsCompleted(index):
                return 'var(--color-info-main)';
            default:
                return 'var(--color-background-standout)';
        }
    };

    // Get the correct color for the text next to the badge depending on context
    const getTextColor = (index: number) => {
        switch (true) {
            case stepHasError(index):
                return 'text-danger';
            case stepIsCurrentStep(index) && isDarkTheme:
                return 'text-base';
            case stepIsCurrentStep(index):
                return 'text-primary';
            default:
                return 'text-light';
        }
    };

    // Get the correct badge kind
    const getBadgeKind = (index: number) => {
        switch (true) {
            case stepHasError(index):
                return 'danger';
            case stepIsCurrentStep(index):
                return 'info';
            case index <= currentStepIndex || stepIsCompleted(index):
                return 'primary';
            default:
                return 'secondary';
        }
    };

    if (orientation === 'vertical') {
        return (
            <div className={clsx('flex flex-col self-center', wrapperStyles)}>
                {steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-4">
                        <div key={index} className="relative flex flex-col items-center">
                            {/* Line above the badge, does not show on first step */}
                            {index !== 0 ? (
                                <div
                                    className={clsx(
                                        styles.verticalBadgeLine,
                                        getLineStyle(index, true)
                                    )}
                                />
                            ) : null}
                            <div
                                key={index}
                                // Add a blue or red pulsing animation to the currently active step when its not yet completed
                                className={clsx(
                                    'relative',
                                    stepHasPulseAnimation(index) ? styles.pulse : null,
                                    stepHasError(index) ? styles.pulseRed : null
                                )}
                            >
                                <Badge
                                    size="small"
                                    kind={getBadgeKind(index)}
                                    icon={stepIcon(index)}
                                    style={[
                                        {
                                            position: 'relative',
                                            backgroundColor: getBackgroundColor(index),
                                            borderWidth: stepIsCurrentStep(index) ? 1 : 0,
                                            borderColor: stepHasError(index)
                                                ? 'var(--color-danger-main)'
                                                : 'var(--color-info-main)',
                                        },
                                    ]}
                                >
                                    {stepNumber(index)}
                                </Badge>
                            </div>

                            {/* Line below badge, does not show on last step */}
                            {index !== steps.length - 1 ? (
                                <div
                                    className={clsx(
                                        styles.verticalBadgeLine,
                                        getLineStyle(index, false)
                                    )}
                                />
                            ) : null}
                        </div>

                        {/* The text next to the badge */}
                        <div
                            className={clsx(
                                getTextColor(index),
                                'text-muted text-ui-action',
                                // Offset the label for first and last badge, where the center is not the same due to only having one connector line
                                index === 0 && 'mb-4',
                                index === steps.length - 1 && 'mt-4'
                            )}
                        >
                            {step.title}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Horizontal
    return (
        <div className={clsx('flex', wrapperStyles)}>
            {steps.map((step, index) => (
                <div key={index} className="relative flex flex-1 flex-col items-center">
                    {/* Left connector line, does not show on first step */}
                    {index !== 0 ? (
                        <div
                            className={clsx(
                                styles.horizontalBadgeLine,
                                styles.lineBeforeBadge,
                                getLineStyle(index, true, true)
                            )}
                        />
                    ) : null}

                    {/* Right connector line, does not show on last step */}
                    {index !== steps.length - 1 ? (
                        <div
                            className={clsx(
                                styles.horizontalBadgeLine,
                                styles.lineAfterBadge,
                                getLineStyle(index, false, true)
                            )}
                        />
                    ) : null}
                    <div
                        key={index}
                        // Add a blue or red pulsing animation to the currently active step when its not yet completed
                        className={clsx(
                            'relative',
                            stepHasPulseAnimation(index) ? styles.pulse : {},
                            stepHasError(index) ? styles.pulseRed : {}
                        )}
                    >
                        <Badge
                            size="small"
                            kind={getBadgeKind(index)}
                            icon={stepIcon(index)}
                            style={[
                                {
                                    backgroundColor: getBackgroundColor(index),
                                    borderWidth: stepIsCurrentStep(index) ? 1 : 0,
                                    borderColor: stepHasError(index)
                                        ? 'var(--color-danger-main)'
                                        : 'var(--color-info-main)',
                                },
                            ]}
                        >
                            {stepNumber(index)}
                        </Badge>
                    </div>

                    {/* The text below to the badge */}
                    <div
                        className={clsx(
                            getTextColor(index),
                            'mt-2 flex items-center text-center text-ui-action'
                        )}
                    >
                        {step.title}
                    </div>
                </div>
            ))}
        </div>
    );
}
